import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { getFileIconMeta } from '@/features/documents/format'

interface DocumentReferenceChipProps {
  /** The document's name, as the answer wrote it. */
  children: ReactNode
  /** Opens the document's detail. Without it the name stays plain text. */
  onOpen?: () => void
}

/** The file type a name ends in: `IMG_8848.jpeg` is `jpeg`. */
function typeOf(name: ReactNode): string | null {
  const text = typeof name === 'string' ? name : Array.isArray(name) ? name.join('') : ''
  const dot = text.lastIndexOf('.')
  return dot > 0 ? text.slice(dot + 1).trim() : null
}

/**
 * A case document named in the assistant's answer: its name with the icon of its type, in the
 * line of text. Selecting it opens the document's detail over the page the chat is on.
 */
export function DocumentReferenceChip({ children, onOpen }: DocumentReferenceChipProps) {
  const { t } = useTranslation()
  if (!onOpen) return <span>{children}</span>
  const { Icon } = getFileIconMeta(typeOf(children))
  return (
    <button
      type="button"
      onClick={onOpen}
      title={t('investigations.entityDetail.openDocumentDetail')}
      className="mx-0.5 inline-flex max-w-full items-center gap-1 rounded-md border border-border bg-muted/50 px-1.5 py-0.5 align-baseline font-medium text-primary text-xs transition-colors hover:border-primary hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Icon aria-hidden className="size-3.5 shrink-0" />
      <span className="truncate">{children}</span>
    </button>
  )
}
